'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { eegMetricsService, EEGMetricRow } from '@/lib/services/eegMetricsService';

interface ChartPoint {
  time: string;
  cognitiveLoad: number;
  attentionScore: number;
  signalQuality: number | null;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function toChartPoint(row: EEGMetricRow): ChartPoint {
  return {
    time: formatTime(row.recordedAt),
    cognitiveLoad: Math.round(row.cognitiveLoad * 100),
    attentionScore: Math.round(row.attentionScore * 100),
    signalQuality: row.signalQuality !== null ? Math.round(row.signalQuality * 100) : null,
  };
}

const CustomTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[#1E3A5F]/80 bg-[#0B1628]/98 px-3 py-2.5 text-[11px] shadow-2xl"
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(249,115,22,0.1)' }}
    >
      <p className="text-[#2D5A8E] mb-2 font-mono font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
          <span className="text-zinc-400">{entry.name}:</span>
          <span className="font-bold text-white">{entry.value}%</span>
        </div>
      ))}
    </div>
  );
};

export default function HistoricalTrendsChart() {
  const [data, setData] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await eegMetricsService.fetchRecent(60);
      setData(rows.map(toChartPoint));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load historical data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const hasSignalQuality = data.some((d) => d.signalQuality !== null);

  return (
    <div className="rounded-xl border border-[#1E3A5F]/60 bg-[#0B1628]/80 p-4 backdrop-blur"
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(249,115,22,0.04)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-[#F97316]" />
          <span className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">
            Historical Trends
          </span>
        </div>
        <div className="flex items-center gap-2">
          {loading && (
            <RefreshCw size={11} className="text-[#F97316]/60 animate-spin" />
          )}
          <span className="text-[10px] text-[#2D5A8E] font-mono">Last {data.length} readings</span>
        </div>
      </div>

      {/* Chart */}
      {error ? (
        <div className="flex items-center justify-center h-48">
          <p className="text-[11px] text-red-400/80 font-mono">⚠ {error}</p>
        </div>
      ) : data.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <div className="w-8 h-8 rounded-full border border-[#1E3A5F] flex items-center justify-center">
            <TrendingUp size={14} className="text-[#2D5A8E]" />
          </div>
          <p className="text-[11px] text-[#2D5A8E]">No data yet — waiting for EEG readings…</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" vertical={false} opacity={0.5} />
            <XAxis
              dataKey="time"
              tick={{ fill: '#2D5A8E', fontSize: 9, fontFamily: 'monospace' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#2D5A8E', fontSize: 9 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '10px', color: '#2D5A8E', paddingTop: '8px' }}
              iconType="circle"
              iconSize={7}
            />
            <Line
              type="monotone"
              dataKey="cognitiveLoad"
              name="Cognitive Load"
              stroke="#a78bfa"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#a78bfa' }}
            />
            <Line
              type="monotone"
              dataKey="attentionScore"
              name="Attention Score"
              stroke="#38bdf8"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#38bdf8' }}
            />
            {hasSignalQuality && (
              <Line
                type="monotone"
                dataKey="signalQuality"
                name="Signal Quality"
                stroke="#34d399"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: '#34d399' }}
                connectNulls={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
